import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import useApi from "hooks/useApi";
import useApp from "hooks/useApp";
import {
    fetchProdutoGheList,
    adicionarProdutoAoGrupo,
    removerProdutoDoGrupo
} from "./ProdutoGhe.Service";

export default function ProdutoGheDialog({ visible, onHide, product }) {
    const api = useApi();
    const app = useApp();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [retryCount, setRetryCount] = useState(0);
    const [actionLoading, setActionLoading] = useState({});

    const productId = product?.id;

    useEffect(() => {
        if (!visible || !productId) {
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError(null);
        setGlobalFilter("");

        fetchProdutoGheList(api, productId)
            .then((data) => {
                if (isMounted) {
                    setRecords(data || []);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    const msg =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Erro ao consultar informações do GHE.";
                    setError(msg);
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
        // Dependemos exclusivamente de visible, productId e retryCount para evitar loops de renderização
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, productId, retryCount]);

    const handleRetry = () => {
        setRetryCount((prev) => prev + 1);
    };

    const handleFieldChange = (rowIndex, field, value) => {
        setRecords((prev) => {
            const next = [...prev];
            next[rowIndex] = {
                ...next[rowIndex],
                [field]: value
            };
            return next;
        });
    };

    const handleToggleStatus = async (rowData, rowIndex) => {
        const groupId = rowData.groupId ?? rowData.grupoid ?? rowData.groupid ?? rowData.id;
        if (!groupId || !product?.id) {
            app.flashMsg("Identificador do grupo ou produto inválido.", "error");
            return;
        }

        if (rowData.isHabilitado) {
            // Ação: Remover/Desvincular (Status era SIM -> vai virar NÃO)
            const confirmed = await app.dialog.Confirm(
                "Confirmação",
                `Deseja desabilitar o produto "${product.nome || product.codexterno}" do grupo "${rowData.nome}"?`
            );
            if (!confirmed) return;

            setActionLoading((prev) => ({ ...prev, [groupId]: true }));
            try {
                await removerProdutoDoGrupo(api, {
                    groupid: groupId,
                    prodid: product.id
                });

                // Sucesso: inverte para NÃO e campos tornam-se editáveis
                setRecords((prev) => {
                    const next = [...prev];
                    next[rowIndex] = {
                        ...next[rowIndex],
                        isHabilitado: false,
                        status: "NÃO"
                    };
                    return next;
                });
                app.flashMsg("Produto desvinculado com sucesso!");
            } catch (err) {
                const msg =
                    err?.response?.data?.xmessage ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Erro ao desvincular produto.";
                app.flashMsg(msg, "error");
            } finally {
                setActionLoading((prev) => ({ ...prev, [groupId]: false }));
            }
        } else {
            // Ação: Adicionar/Vincular (Status era NÃO -> vai virar SIM)
            const qtdeVal = parseInt(rowData.qtde, 10);
            const periodicVal = parseInt(rowData.periodic, 10);

            if (isNaN(qtdeVal) || qtdeVal <= 0) {
                app.flashMsg("Informe uma Quantidade válida (número inteiro maior que zero).", "error");
                return;
            }

            if (isNaN(periodicVal) || periodicVal <= 0) {
                app.flashMsg("Informe uma Periodicidade válida (número inteiro maior que zero).", "error");
                return;
            }

            const confirmed = await app.dialog.Confirm(
                "Confirmação",
                `Deseja habilitar o produto "${product.nome || product.codexterno}" no grupo "${rowData.nome}" com Qtde: ${qtdeVal} e Periodicidade: ${periodicVal} dias?`
            );
            if (!confirmed) return;

            setActionLoading((prev) => ({ ...prev, [groupId]: true }));
            try {
                await adicionarProdutoAoGrupo(api, {
                    grupoid: groupId,
                    prodid: product.id,
                    qtde: qtdeVal,
                    periodic: periodicVal
                });

                // Sucesso: inverte para SIM e campos tornam-se somente leitura
                setRecords((prev) => {
                    const next = [...prev];
                    next[rowIndex] = {
                        ...next[rowIndex],
                        qtde: qtdeVal,
                        periodic: periodicVal,
                        isHabilitado: true,
                        status: "SIM"
                    };
                    return next;
                });
                app.flashMsg("Produto vinculado com sucesso!");
            } catch (err) {
                const msg =
                    err?.response?.data?.xmessage ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Erro ao vincular produto.";
                app.flashMsg(msg, "error");
            } finally {
                setActionLoading((prev) => ({ ...prev, [groupId]: false }));
            }
        }
    };

    const qtdeBodyTemplate = (rowData, options) => {
        if (rowData.isHabilitado) {
            return (
                <div className="flex justify-content-center">
                    <span className="font-semibold text-900">{rowData.qtde}</span>
                </div>
            );
        }

        return (
            <div className="flex justify-content-center">
                <InputNumber
                    value={typeof rowData.qtde === "number" ? rowData.qtde : parseInt(rowData.qtde, 10) || 1}
                    onValueChange={(e) => handleFieldChange(options.rowIndex, "qtde", e.value)}
                    min={1}
                    useGrouping={false}
                    className="p-inputtext-sm text-center"
                    inputStyle={{ width: "70px", textAlign: "center", padding: "4px 6px" }}
                />
            </div>
        );
    };

    const periodicBodyTemplate = (rowData, options) => {
        if (rowData.isHabilitado) {
            return (
                <div className="flex justify-content-center">
                    <span className="font-semibold text-900">{rowData.periodic} dias</span>
                </div>
            );
        }

        return (
            <div className="flex justify-content-center">
                <InputNumber
                    value={typeof rowData.periodic === "number" ? rowData.periodic : parseInt(rowData.periodic, 10) || 90}
                    onValueChange={(e) => handleFieldChange(options.rowIndex, "periodic", e.value)}
                    min={1}
                    useGrouping={false}
                    suffix=" d"
                    className="p-inputtext-sm text-center"
                    inputStyle={{ width: "85px", textAlign: "center", padding: "4px 6px" }}
                />
            </div>
        );
    };

    const statusBodyTemplate = (rowData, options) => {
        const isSim = rowData.isHabilitado;
        const groupId = rowData.groupId ?? rowData.grupoid ?? rowData.groupid ?? rowData.id;
        const isLoading = !!actionLoading[groupId];

        const btnStyle = isSim
            ? {
                  backgroundColor: "#0B3C5D", // Azul marinho
                  borderColor: "#0B3C5D",
                  color: "#ffffff",
                  minWidth: "80px"
              }
            : {
                  backgroundColor: "#c62828", // Vermelho
                  borderColor: "#c62828",
                  color: "#ffffff",
                  minWidth: "80px"
              };

        return (
            <div className="flex justify-content-end w-full">
                <Button
                    type="button"
                    label={rowData.status}
                    icon={isSim ? "pi pi-check" : "pi pi-times"}
                    style={btnStyle}
                    className="p-button-sm font-bold shadow-1"
                    loading={isLoading}
                    tooltip={
                        isSim
                            ? "Clique para desabilitar (remover vínculo)"
                            : "Clique para habilitar (vincular ao grupo)"
                    }
                    tooltipOptions={{ position: "left" }}
                    onClick={() => handleToggleStatus(rowData, options.rowIndex)}
                />
            </div>
        );
    };

    const headerContent = (
        <div className="flex flex-column gap-1">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-shield text-primary text-xl" />
                <span className="font-bold text-lg text-900">GDE / GHE do Produto</span>
            </div>
            {product && (
                <div className="text-sm text-500 font-normal">
                    <span className="font-medium text-700">Código:</span> {product.codexterno || product.id} &nbsp;|&nbsp;{" "}
                    <span className="font-medium text-700">Produto:</span> {product.nome || "Não informado"}
                </div>
            )}
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2 pt-2">
            <Button
                label="Fechar"
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={onHide}
            />
        </div>
    );

    return (
        <Dialog
            header={headerContent}
            visible={visible}
            onHide={onHide}
            modal
            style={{ width: "700px" }}
            breakpoints={{ "960px": "85vw", "640px": "96vw" }}
            footer={dialogFooter}
            className="p-fluid"
            dismissableMask
        >
            <div className="py-2">
                {/* Filtro rápido local */}
                <div className="mb-3">
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search" />
                        <InputText
                            type="search"
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Pesquisar GDE..."
                            className="p-inputtext-sm w-full"
                            disabled={loading || records.length === 0}
                        />
                    </span>
                </div>

                {loading ? (
                    <div className="flex flex-column align-items-center justify-content-center py-5 gap-2 text-gray-500">
                        <ProgressSpinner style={{ width: "42px", height: "42px" }} />
                        <span className="text-sm font-medium">Consultando grupos vinculados...</span>
                    </div>
                ) : error ? (
                    <div className="p-3 text-center border-round surface-100 border-1 surface-border my-2">
                        <i className="pi pi-exclamation-triangle text-red-500 text-3xl mb-2" />
                        <div className="font-medium text-red-600 mb-2">{error}</div>
                        <Button
                            label="Tentar Novamente"
                            icon="pi pi-refresh"
                            className="p-button-sm p-button-outlined"
                            onClick={handleRetry}
                        />
                    </div>
                ) : (
                    <>
                        <style>{`
                            .ghe-datatable {
                                width: 100% !important;
                            }
                            .ghe-datatable .p-datatable-wrapper,
                            .ghe-datatable .p-datatable-table,
                            .ghe-datatable .p-datatable-scrollable-header-table,
                            .ghe-datatable .p-datatable-scrollable-body-table {
                                width: 100% !important;
                                min-width: 100% !important;
                            }
                            .ghe-datatable .ghe-col-right {
                                text-align: right !important;
                            }
                            .ghe-datatable .ghe-col-right .p-column-header-content {
                                justify-content: flex-end !important;
                            }
                        `}</style>
                        <DataTable
                            value={records}
                            globalFilter={globalFilter}
                            responsiveLayout="scroll"
                            stripedRows
                            size="small"
                            emptyMessage="Nenhum grupo (GDE) vinculado encontrado para este produto."
                            className="p-datatable-sm ghe-datatable w-full"
                            style={{ width: "100%" }}
                            tableStyle={{ width: "100%", minWidth: "100%" }}
                            scrollable
                            scrollHeight="340px"
                        >
                            <Column
                                field="nome"
                                header="GDE"
                                sortable
                                headerStyle={{ textAlign: "left", fontWeight: 700 }}
                                bodyStyle={{ textAlign: "left", fontWeight: 500 }}
                            />
                            <Column
                                field="qtde"
                                header="QTDE"
                                body={qtdeBodyTemplate}
                                sortable
                                style={{ width: "100px" }}
                                headerStyle={{ width: "100px", textAlign: "center", fontWeight: 700 }}
                                bodyStyle={{ width: "100px", textAlign: "center" }}
                            />
                            <Column
                                field="periodic"
                                header="PERIODICIDADE"
                                body={periodicBodyTemplate}
                                sortable
                                style={{ width: "135px" }}
                                headerStyle={{ width: "135px", textAlign: "center", fontWeight: 700 }}
                                bodyStyle={{ width: "135px", textAlign: "center" }}
                            />
                            <Column
                                field="status"
                                header={
                                    <div className="flex align-items-center justify-content-end w-full">
                                        <span>HABILITADO</span>
                                    </div>
                                }
                                body={statusBodyTemplate}
                                sortable
                                style={{ width: "130px" }}
                                headerStyle={{ width: "130px", textAlign: "right", fontWeight: 700 }}
                                headerClassName="ghe-col-right"
                                bodyStyle={{ width: "130px", textAlign: "right" }}
                            />
                        </DataTable>
                    </>
                )}
            </div>
        </Dialog>
    );
}

ProdutoGheDialog.propTypes = {
    visible: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nome: PropTypes.string,
        codexterno: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
};

ProdutoGheDialog.defaultProps = {
    product: null
};
